import { useState, type ReactNode } from 'react'
import {
  ACT_CONTENT,
  TRUST_BEATS,
  UI_CONTENT,
  WORLD_CONTENT,
  localize,
} from '../content/experienceContent'
import { ACT_RANGES, type Locale } from '../lib/sceneStore'

export interface ExperienceProps {
  locale: Locale
  activeAct: number
  progress: number
  demoRequested: boolean
  demoActive: boolean
  demoPaused: boolean
  verticalCapture: boolean
  onLocaleChange: (locale: Locale) => void
  onDemoToggle: () => void
  onDemoRestart: () => void
}

interface ActShellProps {
  index: number
  locale: Locale
  activeAct: number
  children: ReactNode
}

function ActShell({
  index,
  locale,
  activeAct,
  children,
}: ActShellProps) {
  const act = ACT_CONTENT[index]
  const Heading = index === 0 ? 'h1' : 'h2'
  const isActive = index === activeAct

  return (
    <section
      id={act.id}
      className={[
        'act',
        `act--${act.number}`,
        isActive ? 'is-active' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-labelledby={`${act.id}-title`}
      data-act-index={index}
      data-film-label={act.filmLabel.en}
      data-active={isActive ? 'true' : 'false'}
    >
      <div className="act__pin">
        <div className="act__frame">
          <div className="act__copy">
            <p className="film-label">
              <span className="film-label__number" aria-hidden="true">
                {act.number}
              </span>
              <span className="film-label__text">
                {localize(act.filmLabel, locale)}
              </span>
            </p>
            <Heading id={`${act.id}-title`} className="act__title">
              {localize(act.title, locale)}
            </Heading>
            <p className="act__body">{localize(act.body, locale)}</p>
          </div>
          {children}
        </div>
      </div>
    </section>
  )
}

function getSafeAct(activeAct: number): number {
  if (!Number.isFinite(activeAct)) return 0
  return Math.max(0, Math.min(ACT_CONTENT.length - 1, Math.trunc(activeAct)))
}

export function Experience({
  locale,
  activeAct,
  progress,
  demoRequested,
  demoActive,
  demoPaused,
  verticalCapture,
  onLocaleChange,
  onDemoToggle,
  onDemoRestart,
}: ExperienceProps) {
  const [selectedTrustBeat, setSelectedTrustBeat] = useState(0)
  const safeActiveAct = getSafeAct(activeAct)
  const worldRange = ACT_RANGES[4]
  const worldProgress = Math.max(
    0,
    Math.min(
      0.9999,
      (progress - worldRange.start) / (worldRange.end - worldRange.start),
    ),
  )
  const activeWorldIndex = Math.floor(worldProgress * WORLD_CONTENT.length)
  const direction = locale === 'ar' ? 'rtl' : 'ltr'
  const activeFilmLabel = localize(
    ACT_CONTENT[safeActiveAct].filmLabel,
    locale,
  )
  const demoState = !demoRequested
    ? UI_CONTENT.demoOff
    : demoActive && demoPaused
      ? UI_CONTENT.demoPaused
      : demoActive
        ? UI_CONTENT.demoPlaying
        : UI_CONTENT.demoReady
  const demoStatus =
    locale === 'ar'
      ? `${localize(demoState, locale)} المشهد ${safeActiveAct + 1} من ${ACT_CONTENT.length}: ${activeFilmLabel}. ${verticalCapture ? localize(UI_CONTENT.verticalCapture, locale) : ''}`
      : `${localize(demoState, locale)} Act ${safeActiveAct + 1} of ${ACT_CONTENT.length}: ${activeFilmLabel}. ${verticalCapture ? localize(UI_CONTENT.verticalCapture, locale) : ''}`
  const demoToggleLabel =
    demoActive && demoPaused
      ? UI_CONTENT.demoResume
      : demoActive
        ? UI_CONTENT.demoPause
        : UI_CONTENT.demoStart

  return (
    <div
      className={[
        'experience',
        `experience--${locale}`,
        verticalCapture ? 'experience--vertical' : '',
        demoActive ? 'is-demo-active' : '',
        demoPaused ? 'is-demo-paused' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      lang={locale}
      dir={direction}
      data-active-act={safeActiveAct}
      data-demo-requested={demoRequested ? 'true' : 'false'}
    >
      <a className="skip-link" href="#experience-main">
        {localize(UI_CONTENT.skip, locale)}
      </a>

      <header className="site-header" data-capture-ui="hide">
        <a
          className="site-brand"
          href="#act-00"
          aria-label={localize(UI_CONTENT.brandLabel, locale)}
          dir="ltr"
        >
          <span className="site-brand__mark" aria-hidden="true">
            A/
          </span>
          <span className="site-brand__name">Avatar Platform</span>
        </a>

        <div className="site-header__controls">
          <div
            className="locale-switcher"
            role="group"
            aria-label={localize(UI_CONTENT.languageLabel, locale)}
          >
            <button
              className={[
                'locale-switcher__option',
                locale === 'en' ? 'is-active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              type="button"
              lang="en"
              dir="ltr"
              aria-pressed={locale === 'en'}
              onClick={() => onLocaleChange('en')}
            >
              EN
              <span className="sr-only">
                {localize(UI_CONTENT.english, locale)}
              </span>
            </button>
            <button
              className={[
                'locale-switcher__option',
                locale === 'ar' ? 'is-active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              type="button"
              lang="ar"
              dir="rtl"
              aria-pressed={locale === 'ar'}
              onClick={() => onLocaleChange('ar')}
            >
              ع
              <span className="sr-only">
                {localize(UI_CONTENT.arabic, locale)}
              </span>
            </button>
          </div>

          <span className="site-header__act" aria-hidden="true">
            {ACT_CONTENT[safeActiveAct].number} / 08
          </span>
        </div>
      </header>

      <nav
        className="progress-rail"
        aria-label={localize(UI_CONTENT.progressLabel, locale)}
        data-capture-ui="hide"
      >
        <ol className="progress-rail__list">
          {ACT_CONTENT.map((act, index) => {
            const isActive = index === safeActiveAct

            return (
              <li
                className={[
                  'progress-rail__item',
                  isActive ? 'is-active' : '',
                  index < safeActiveAct ? 'is-past' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                key={act.id}
              >
                <a
                  className="progress-rail__link"
                  href={`#${act.id}`}
                  aria-current={isActive ? 'step' : undefined}
                >
                  <span className="progress-rail__track" aria-hidden="true" />
                  <span className="progress-rail__number" aria-hidden="true">
                    {act.number}
                  </span>
                  <span className="progress-rail__label">
                    {localize(act.filmLabel, locale)}
                  </span>
                </a>
              </li>
            )
          })}
        </ol>
      </nav>

      <div
        className={[
          'demo-controls',
          demoRequested ? 'is-requested' : '',
          demoActive ? 'is-active' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        role="group"
        aria-label={localize(UI_CONTENT.demoControls, locale)}
        data-capture-ui="hide"
      >
        <button
          className="demo-controls__toggle"
          type="button"
          onClick={onDemoToggle}
        >
          {localize(demoToggleLabel, locale)}
        </button>
        <button
          className="demo-controls__restart"
          type="button"
          onClick={onDemoRestart}
          disabled={!demoRequested}
        >
          {localize(UI_CONTENT.demoRestart, locale)}
        </button>
      </div>

      <p
        className="demo-status sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {demoStatus.trim()}
      </p>

      {verticalCapture && demoRequested ? (
        <p className="sr-only">
          {localize(UI_CONTENT.captureKeyboardHelp, locale)}
        </p>
      ) : null}

      <main id="experience-main" className="experience__main" tabIndex={-1}>
        <ActShell index={0} locale={locale} activeAct={safeActiveAct}>
          <div className="first-contact" aria-hidden="true">
            <span className="first-contact__halo" />
            <span className="first-contact__profile" />
            <span className="first-contact__lattice" />
            <span className="first-contact__scan" />
          </div>
        </ActShell>

        <ActShell index={1} locale={locale} activeAct={safeActiveAct}>
          <div className="photo-depth" aria-hidden="true">
            <span className="photo-depth__source" />
            {Array.from({ length: 7 }, (_, index) => (
              <span
                className="photo-depth__layer"
                data-depth={index + 1}
                key={index}
              />
            ))}
            <span className="photo-depth__landmarks" />
          </div>
        </ActShell>

        <ActShell index={2} locale={locale} activeAct={safeActiveAct}>
          <div className="voice-activation" aria-hidden="true">
            <span className="voice-activation__ring voice-activation__ring--01" />
            <span className="voice-activation__ring voice-activation__ring--02" />
            <span className="voice-activation__ring voice-activation__ring--03" />
            <span className="voice-activation__pulse" />
          </div>
        </ActShell>

        <ActShell index={3} locale={locale} activeAct={safeActiveAct}>
          <div className="identity-rupture" aria-hidden="true">
            <span className="identity-rupture__lattice" />
            {Array.from({ length: 12 }, (_, index) => (
              <span
                className={`identity-rupture__plate identity-rupture__plate--${String(index + 1).padStart(2, '0')}`}
                key={index}
              />
            ))}
            <span className="identity-rupture__cluster identity-rupture__cluster--real" />
            <span className="identity-rupture__cluster identity-rupture__cluster--styled" />
            <span className="identity-rupture__cluster identity-rupture__cluster--subject" />
          </div>
        </ActShell>

        <ActShell index={4} locale={locale} activeAct={safeActiveAct}>
          <div
            className="world-sequence"
            role="list"
            aria-label={localize(UI_CONTENT.worldSequence, locale)}
          >
            {WORLD_CONTENT.map((world, index) => (
              <article
                className={[
                  'world-stage',
                  `world-stage--${world.id}`,
                  index === activeWorldIndex ? 'is-current' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                role="listitem"
                data-world-index={index}
                aria-current={index === activeWorldIndex ? 'step' : undefined}
                key={world.id}
              >
                <div className="world-stage__material" aria-hidden="true">
                  <span className="world-stage__shell" />
                  <span className="world-stage__lattice" />
                  <span className="world-stage__transition" />
                </div>
                <div className="world-stage__copy">
                  <span className="world-stage__number" aria-hidden="true">
                    {world.number} / 03
                  </span>
                  <h3 className="world-stage__title">
                    {localize(world.name, locale)}
                  </h3>
                  <p className="world-stage__line">
                    {localize(world.line, locale)}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </ActShell>

        <ActShell index={5} locale={locale} activeAct={safeActiveAct}>
          <figure className="reel-assembly">
            <div className="reel-assembly__scatter">
              {WORLD_CONTENT.map((world, index) => (
                <div
                  className={`reel-source reel-source--${world.id}`}
                  data-source-index={index}
                  key={world.id}
                >
                  <picture>
                    <source
                      media="(max-width: 700px)"
                      srcSet={world.mobileImageUrl}
                      type="image/webp"
                    />
                    <img
                      className="reel-source__image"
                      src={world.imageUrl}
                      alt={`${localize(world.name, locale)} — ${localize(world.line, locale)}`}
                      loading="lazy"
                      decoding="async"
                    />
                  </picture>
                  <span className="reel-source__edge" aria-hidden="true" />
                </div>
              ))}

              {Array.from({ length: 6 }, (_, index) => (
                <span
                  className={`reel-plate reel-plate--${String(index + 1).padStart(2, '0')}`}
                  aria-hidden="true"
                  key={index}
                />
              ))}

              <div className="reel-assembly__timing" aria-hidden="true">
                <span />
                <span />
                <span />
                <span />
              </div>
            </div>

            <div className="reel-output" aria-hidden="true">
              <span className="reel-output__frame" />
              <span className="reel-output__caption" />
              <span className="reel-output__playhead" />
            </div>

            <figcaption className="sr-only">
              {localize(UI_CONTENT.reelFigure, locale)}
            </figcaption>
          </figure>
        </ActShell>

        <ActShell index={6} locale={locale} activeAct={safeActiveAct}>
          <div className="automation-orbit" aria-hidden="true">
            <span className="automation-orbit__path automation-orbit__path--01" />
            <span className="automation-orbit__path automation-orbit__path--02" />
            <span className="automation-orbit__path automation-orbit__path--03" />
            {Array.from({ length: 5 }, (_, index) => (
              <span
                className={`automation-orbit__output automation-orbit__output--${String(index + 1).padStart(2, '0')}`}
                key={index}
              />
            ))}
            <span className="automation-orbit__core" />
          </div>
        </ActShell>

        <ActShell index={7} locale={locale} activeAct={safeActiveAct}>
          <div
            className="trust-sequence"
            role="group"
            aria-label={localize(UI_CONTENT.trustSequence, locale)}
          >
            <span className="trust-sequence__approval-plane" aria-hidden="true" />
            <div className="trust-sequence__beats">
              {TRUST_BEATS.map((beat, index) => {
                const isSelected = index === selectedTrustBeat

                return (
                  <button
                    className={[
                      'trust-beat',
                      `trust-beat--${beat.id}`,
                      isSelected ? 'is-selected' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => setSelectedTrustBeat(index)}
                    key={beat.id}
                  >
                    <span className="trust-beat__number" aria-hidden="true">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <span className="trust-beat__title">
                      {localize(beat.title, locale)}
                    </span>
                    <span className="trust-beat__body">
                      {localize(beat.body, locale)}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        </ActShell>

        <ActShell index={8} locale={locale} activeAct={safeActiveAct}>
          <div className="finale">
            <div className="finale__presence" aria-hidden="true">
              <span className="finale__shell" />
              <span className="finale__lattice" />
              <span className="finale__aperture" />
            </div>
            <a
              className="primary-cta"
              href="https://app.samer-ai.com"
              aria-label={localize(UI_CONTENT.primaryCtaLabel, locale)}
              data-magnetic="true"
            >
              <span className="primary-cta__label">
                {localize(UI_CONTENT.primaryCta, locale)}
              </span>
              <span className="primary-cta__arrow" aria-hidden="true">
                ↗
              </span>
            </a>
          </div>
        </ActShell>
      </main>
    </div>
  )
}
